import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {DirectoryDataService} from 'shared/data-services';
import type {FinishedResponseInteractionService} from 'shared/services';
import type {Directory, DirectoryNode, Form, Rec, ReportTemplate, ServerFolder} from 'src/typings';
import type {FileService} from 'src/features/file/file.service';
import {serializeUrlParams, transferify} from 'shared/utils/data-service-utils';
import {exists} from 'shared/utils/custom-utils';
import {DateTime} from 'luxon';
import ReportServerDataService from './report-server.data-service';

export default class ReportServerService {
    public static serviceName = 'reportServerService';

    private static readonly exportsFolderName = 'Exporty';

    /*@ngInject*/
    constructor(private toastMessageService: ToastMessageService,
                private reportServerDataService: ReportServerDataService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private directoryDataService: DirectoryDataService,
                private fileService: FileService) {
    }

    public async getServerFolder(): Promise<ServerFolder> {
        try {
            return await this.reportServerDataService.getServerFolder();
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async getRecordReportServerFolder(record: Rec): Promise<ServerFolder> {
        try {
            return await this.reportServerDataService.getFondReportFolder(record.fond);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async getTemplateForm(report: ReportTemplate): Promise<Form> {
        return this.reportServerDataService.getTemplateForm(report);
    }

    public async downloadExport(report: ReportTemplate, props: Record<string, any>, filename: string | null = null): Promise<void> {
        const waitToast = this.toastMessageService.showWait('Probíhá generování přehledu...');

        try {
            await this.reportServerDataService.downloadExportFile(report, props, filename);

            this.toastMessageService.dismissToast(waitToast);
            this.toastMessageService.showSuccess('Přehled byl úspěšně vygenerován');
        } catch (exceptionResponse) {
            this.toastMessageService.dismissToast(waitToast);
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        }
    }

    public async getBlobExport(report: ReportTemplate, props: Record<string, any>, filename: string | null = null): Promise<File | null> {
        const waitToast = this.toastMessageService.showWait('Probíhá generování přehledu...');

        try {
            const blob = await this.reportServerDataService.getBlobExportFile(report, props, filename);

            this.toastMessageService.dismissToast(waitToast);
            this.toastMessageService.showSuccess('Přehled byl úspěšně vygenerován');

            return blob;
        } catch (exceptionResponse) {
            this.toastMessageService.dismissToast(waitToast);
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }
    }

    public async saveExportToRecordFiles(record: Rec, template: ReportTemplate, exportData: Record<string, any>): Promise<void> {
        try {
            const uploadDirectory = await this.getOrCreateExportsDirectory(record);
            const dateNow = DateTime.now();
            const exportedFile = await this.reportServerDataService.getBlobExportFile(template, exportData);
            const timestamp = dateNow.toFormat('yyyy-MM-dd_HH-mm');

            const fileNameSplit = exportedFile.name.split('.');
            const fileNameWithoutExtension = fileNameSplit.length > 1 ? fileNameSplit.slice(0, -1).join('.') : fileNameSplit.join('.');
            const fileExtension = fileNameSplit.length > 1 ? `.${fileNameSplit[fileNameSplit.length - 1]}` : '';

            const fileName = `${fileNameWithoutExtension}-${timestamp}${fileExtension}`;
            const renamedFile = new File([exportedFile], fileName, {type: exportedFile.type});

            await this.fileService.uploadFileToDirectory(uploadDirectory.id, renamedFile);
            this.toastMessageService.showSuccess('Přehled byl úspěšně uložen');
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        }
    }

    private async getOrCreateExportsDirectory(projectRecord: Rec): Promise<Directory | null> {
        const directoryTree = await this.directoryDataService.getDirectoryTree(projectRecord.directoryId);
        const foundDirectory = directoryTree.children.find((child) => child.name === ReportServerService.exportsFolderName);

        let directory: DirectoryNode | Directory = foundDirectory;

        if (!exists(foundDirectory)) {
            const result = await this.directoryDataService.createSubdirectory(projectRecord.directoryId, ReportServerService.exportsFolderName);
            directory = result.savedObject;
        }

        if (!exists(directory)) {
            this.toastMessageService.showError('Nastala chyba při vytváření složky pro exporty!');
            return null;
        }

        return directory;
    }

    public getExportUrl(report: ReportTemplate, props: Record<string, any>): string {
        const serializedUrlParams = serializeUrlParams(transferify(props));
        return `/api/${ReportServerDataService.EXPORT_ROUTE_PREFIX}/${report.id}/${ReportServerDataService.EXPORT_ROUTE_SUFFIX}?${serializedUrlParams}`;
    }
}